// SPDX-License-Identifier: AGPL-3.0-only

export type GeographicBounds = readonly [west: number, south: number, east: number, north: number];

export interface DemGrid {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly bounds: GeographicBounds;
  readonly elevations: Float32Array;
  readonly noDataValue?: number;
}

export interface GridMetrics {
  readonly cellWidthMeters: number;
  readonly cellHeightMeters: number;
  readonly cellAreaSquareMeters: number;
}

export interface InflowSource {
  readonly id: string;
  readonly longitude: number;
  readonly latitude: number;
  readonly radiusMeters: number;
  readonly flowRateCubicMetersPerSecond: number;
}

export interface SolverParameters {
  readonly gravity: number;
  readonly roughness: number;
  readonly fluxDamping: number;
  readonly pipeAreaScale: number;
  readonly cfl: number;
  readonly maxTimeStepSeconds: number;
  readonly minimumWetDepthMeters: number;
  readonly openBoundaryDrainRate: number;
  readonly maximumDepthMeters: number;
  readonly adaptiveDomainEnabled: boolean;
  readonly adaptiveDomainTileSizeCells: number;
  readonly adaptiveDomainInitialPaddingCells: number;
  readonly adaptiveDomainEdgeMarginCells: number;
  readonly adaptiveDomainCheckInterval: number;
}

export interface SimulationStatistics {
  readonly elapsedSeconds: number;
  readonly iteration: number;
  readonly wetCellCount: number;
  readonly maximumDepthMeters: number;
  readonly meanWetDepthMeters: number;
  readonly waterVolumeCubicMeters: number;
}

export interface SimulationSnapshot {
  readonly depth: Float32Array;
  readonly velocityEast: Float32Array;
  readonly velocityNorth: Float32Array;
  readonly statistics: SimulationStatistics;
}

export const DEFAULT_SOLVER_PARAMETERS: SolverParameters = {
  gravity: 9.81,
  roughness: 0.03,
  fluxDamping: 1,
  pipeAreaScale: 0.35,
  cfl: 0.35,
  maxTimeStepSeconds: 0.06,
  minimumWetDepthMeters: 0.02,
  openBoundaryDrainRate: 2.4,
  maximumDepthMeters: 10_000,
  adaptiveDomainEnabled: true,
  adaptiveDomainTileSizeCells: 32,
  adaptiveDomainInitialPaddingCells: 8,
  adaptiveDomainEdgeMarginCells: 4,
  adaptiveDomainCheckInterval: 4
};

export function validateDemGrid(grid: DemGrid): void {
  if (!Number.isInteger(grid.width) || !Number.isInteger(grid.height)) {
    throw new Error('DEM width and height must be integers.');
  }
  if (grid.width < 3 || grid.height < 3) {
    throw new Error('DEM grid must contain at least 3 x 3 cells.');
  }
  if (grid.elevations.length !== grid.width * grid.height) {
    throw new Error('DEM elevation count does not match its dimensions.');
  }
  for (const elevation of grid.elevations) {
    if (!Number.isFinite(elevation)) {
      throw new Error('DEM elevations must contain only finite values.');
    }
  }
  const [west, south, east, north] = grid.bounds;
  if (!(west < east && south < north)) {
    throw new Error('DEM bounds must be ordered west, south, east, north.');
  }
}
